/* ==========================================================================
   HMM DELIVERY ENGINE

   One place that answers "when does this arrive, and do we need to upgrade
   the label?" Everything on the storefront that shows a delivery date must
   call this — the PDP reassurance tile, the service-date picker, the cart.

   Exposed as window.HMDelivery. Loaded as a classic deferred script before
   hmm.js so it is ready at DOMContentLoaded.

   TWO NUMBERS THAT MUST MATCH THE PUBLISHED COPY:

     CUTOFF_HOUR_ET = 12   "Orders placed before 12PM EST ... same day"
                           (PDP Delivery accordion, FAQ, footer)

     GROUND_DAYS           UPS Ground business days in transit from 49464.

   If you change either, change the customer-facing copy in the same commit.
   ========================================================================== */

(function () {
  "use strict";

  var TIME_ZONE = "America/New_York";

  /* Order-by cutoff, Eastern. The published promise is noon. */
  var CUTOFF_HOUR_ET = 12;

  /* Ship-from: HMM Main Distribution, Zeeland MI 49464. */
  var ORIGIN_ZIP = "49464";

  /* Arrival must beat the service by this many calendar days. Our own transit
     audit (commit 73f2e96) found median 3.9 days ground with 3 of 22 orders
     past five days, so we never promise an arrival on the service day itself. */
  var BUFFER_DAYS = 1;

  /* ------------------------------------------------------------------------
     ZIP prefix -> state. First three digits, inclusive ranges.
     ------------------------------------------------------------------------ */
  var ZIP_STATE_RANGES = [
    [5, 5, "NY"], [6, 9, "PR"], [10, 27, "MA"], [28, 29, "RI"], [30, 38, "NH"],
    [39, 49, "ME"], [50, 59, "VT"], [60, 69, "CT"], [70, 89, "NJ"],
    [100, 149, "NY"], [150, 196, "PA"], [197, 199, "DE"], [200, 200, "DC"],
    [201, 201, "VA"], [202, 205, "DC"], [206, 219, "MD"], [220, 246, "VA"],
    [247, 268, "WV"], [270, 289, "NC"], [290, 299, "SC"], [300, 319, "GA"],
    [320, 349, "FL"], [350, 369, "AL"], [370, 385, "TN"], [386, 397, "MS"],
    [398, 399, "GA"], [400, 427, "KY"], [430, 459, "OH"], [460, 479, "IN"],
    [480, 499, "MI"], [500, 528, "IA"], [530, 549, "WI"], [550, 567, "MN"],
    [570, 577, "SD"], [580, 588, "ND"], [590, 599, "MT"], [600, 629, "IL"],
    [630, 658, "MO"], [660, 679, "KS"], [680, 693, "NE"], [700, 714, "LA"],
    [716, 729, "AR"], [730, 749, "OK"], [750, 799, "TX"], [800, 816, "CO"],
    [820, 831, "WY"], [832, 838, "ID"], [840, 847, "UT"], [850, 865, "AZ"],
    [870, 884, "NM"], [889, 898, "NV"], [900, 961, "CA"], [967, 968, "HI"],
    [970, 979, "OR"], [980, 994, "WA"], [995, 999, "AK"]
  ];

  /* ------------------------------------------------------------------------
     UPS Ground business days in transit from Zeeland MI (49464).

     ESTIMATED, ROUNDED UP. Not yet reconciled against the official UPS
     time-in-transit map for 49464 — see docs/rush-shipping-plan.md. Rounding
     up is the safe direction: it triggers a rush upgrade slightly too often
     rather than promising a date ground cannot hit.
     ------------------------------------------------------------------------ */
  var GROUND_DAYS = {
    MI: 1, IN: 1, OH: 1,

    IL: 2, WI: 2, KY: 2, PA: 2, NY: 2, NJ: 2, WV: 2, VA: 2, MD: 2, DE: 2,
    DC: 2, TN: 2, IA: 2, MO: 2, MN: 2, NC: 2, CT: 2, MA: 2, RI: 2,

    ME: 3, NH: 3, VT: 3, SC: 3, GA: 3, AL: 3, MS: 3, AR: 3, LA: 3, FL: 3,
    OK: 3, KS: 3, NE: 3, SD: 3, ND: 3, TX: 3, CO: 3, NM: 3, WY: 3, MT: 3,
    UT: 3,

    AZ: 4, NV: 4, ID: 4, CA: 4, OR: 4, WA: 4
  };

  /* AK, HI and PR are deliberately absent from GROUND_DAYS. The Domestic
     shipping zone does not cover them — the Alaska Hawaii zone carries only
     carrier-calculated paid rates, with no free option at all. We never show
     a free-shipping or rush promise there. */

  var GROUND_LABEL = "Free Shipping";

  /* AIR TRANSIT IS NOT GUESSED. An earlier version of this file assumed 2nd Day
     Air always takes two business days and Next Day Air always takes one. Both
     are false out of Zeeland, in two separate ways, and the store's own UPS
     connection says so:

       43210 Columbus OH   no 2nd Day Air offered at all
       57724 Camp Crook SD no 2nd Day Air offered at all
       89049 Tonopah NV    "Next Day Air" commits to the third business day
       83252 Arco ID       "Next Day Air" commits to the third business day

     So air services and their arrival dates come from live carrier rates —
     fetchRates() below — and never from a table. We only ever name a service
     UPS has actually quoted for that ZIP. */

  /* How long a live rate quote stays good. UPS commitments move at most once a
     day; refetching per keystroke would be rude and slow. */
  var RATE_CACHE_MS = 10 * 60 * 1000;

  /* ======================================================================
     Plain dates. { y, m, d } with m 1-12. Never a bare Date, so nothing
     can drift across a timezone boundary.
     ====================================================================== */

  function toUTC(date) {
    return new Date(Date.UTC(date.y, date.m - 1, date.d, 12, 0, 0));
  }

  function fromUTC(utc) {
    return { y: utc.getUTCFullYear(), m: utc.getUTCMonth() + 1, d: utc.getUTCDate() };
  }

  function ordinal(date) {
    return date.y * 10000 + date.m * 100 + date.d;
  }

  function addDays(date, days) {
    var utc = toUTC(date);
    utc.setUTCDate(utc.getUTCDate() + days);
    return fromUTC(utc);
  }

  function weekday(date) {
    return toUTC(date).getUTCDay();
  }

  function parseISO(value) {
    var match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || "").trim());
    if (!match) return null;

    var date = { y: +match[1], m: +match[2], d: +match[3] };

    /* Reject 2026-02-31 and friends. */
    var utc = toUTC(date);
    if (fromUTC(utc).m !== date.m || fromUTC(utc).d !== date.d) return null;

    return date;
  }

  function toISO(date) {
    return date.y + "-" + String(date.m).padStart(2, "0") + "-" + String(date.d).padStart(2, "0");
  }

  /* ======================================================================
     Holidays. UPS does not move ground freight on these.
     ====================================================================== */

  /* Fixed-date holidays shift off the weekend: Sat -> Fri, Sun -> Mon. */
  function observed(date) {
    var day = weekday(date);
    if (day === 6) return addDays(date, -1);
    if (day === 0) return addDays(date, 1);
    return date;
  }

  /* nth (1-based) given weekday of a month; nth = -1 means the last one. */
  function nthWeekday(year, month, targetDay, nth) {
    if (nth === -1) {
      var last = fromUTC(new Date(Date.UTC(year, month, 0, 12, 0, 0)));
      while (weekday(last) !== targetDay) last = addDays(last, -1);
      return last;
    }

    var date = { y: year, m: month, d: 1 };
    while (weekday(date) !== targetDay) date = addDays(date, 1);
    return addDays(date, (nth - 1) * 7);
  }

  var holidayCache = {};

  function holidaysFor(year) {
    if (holidayCache[year]) return holidayCache[year];

    var days = [
      observed({ y: year, m: 1, d: 1 }),      // New Year's Day
      nthWeekday(year, 5, 1, -1),             // Memorial Day, last Monday in May
      observed({ y: year, m: 7, d: 4 }),      // Independence Day
      nthWeekday(year, 9, 1, 1),              // Labor Day, first Monday in September
      nthWeekday(year, 11, 4, 4),             // Thanksgiving, fourth Thursday in November
      observed({ y: year, m: 12, d: 25 })     // Christmas Day
    ];

    holidayCache[year] = days.map(ordinal);
    return holidayCache[year];
  }

  function isHoliday(date) {
    return holidaysFor(date.y).indexOf(ordinal(date)) !== -1;
  }

  function isWeekend(date) {
    var day = weekday(date);
    return day === 0 || day === 6;
  }

  function isBusinessDay(date) {
    return !isWeekend(date) && !isHoliday(date);
  }

  /* A day a carrier can hand a package over, which is NOT the same as a business
     day: UPS delivers on Saturdays across most of the country, but never on
     Sundays or holidays. Used only to sanity-check carrier-quoted arrival dates.
     Saturday is left alone because a Saturday quote is a real commitment UPS
     made; a holiday quote is not. */
  function isDeliveryDay(date) {
    return weekday(date) !== 0 && !isHoliday(date);
  }

  function nextDeliveryDay(date) {
    var result = date;
    while (!isDeliveryDay(result)) result = addDays(result, 1);
    return result;
  }

  function nextBusinessDay(date) {
    var result = date;
    while (!isBusinessDay(result)) result = addDays(result, 1);
    return result;
  }

  function addBusinessDays(date, count) {
    var result = date;
    var added = 0;

    while (added < count) {
      result = addDays(result, 1);
      if (isBusinessDay(result)) added += 1;
    }

    return result;
  }

  /* ======================================================================
     Eastern "now"
     ====================================================================== */

  function nowEastern(reference) {
    var parts = new Intl.DateTimeFormat("en-US", {
      timeZone: TIME_ZONE,
      year: "numeric", month: "numeric", day: "numeric",
      hour: "numeric", minute: "numeric", second: "numeric",
      hour12: false
    }).formatToParts(reference || new Date());

    var data = {};
    parts.forEach(function (part) {
      if (part.type !== "literal") data[part.type] = Number(part.value);
    });

    /* Intl renders midnight as hour 24 in some engines. */
    if (data.hour === 24) data.hour = 0;

    return {
      date: { y: data.year, m: data.month, d: data.day },
      hour: data.hour,
      minute: data.minute,
      second: data.second
    };
  }

  /* The business day we actually hand the package to UPS. */
  function processingDate(now) {
    var today = now.date;

    if (!isBusinessDay(today)) return nextBusinessDay(today);
    if (now.hour >= CUTOFF_HOUR_ET) return nextBusinessDay(addDays(today, 1));

    return today;
  }

  function secondsUntilCutoff(now) {
    var processing = processingDate(now);
    var days = Math.round((toUTC(processing) - toUTC(now.date)) / 86400000);
    var elapsed = now.hour * 3600 + now.minute * 60 + now.second;

    return Math.max(0, days * 86400 + CUTOFF_HOUR_ET * 3600 - elapsed);
  }

  /* ======================================================================
     Destination
     ====================================================================== */

  function stateForZip(zip) {
    var digits = String(zip || "").replace(/\D/g, "");
    if (digits.length < 5) return null;

    var prefix = Number(digits.slice(0, 3));

    for (var i = 0; i < ZIP_STATE_RANGES.length; i++) {
      var range = ZIP_STATE_RANGES[i];
      if (prefix >= range[0] && prefix <= range[1]) return range[2];
    }

    return null;
  }

  function groundDaysForZip(zip) {
    var state = stateForZip(zip);
    if (!state) return null;

    return Object.prototype.hasOwnProperty.call(GROUND_DAYS, state) ? GROUND_DAYS[state] : null;
  }

  /* ======================================================================
     Estimates
     ====================================================================== */

  /* Free ground for this destination. The flat $0.00 Ground rate carries no
     delivery estimate of its own, so this one number stays table-derived — it
     is the only estimated figure left in the system. */
  function estimate(options) {
    var opts = options || {};
    var now = nowEastern(opts.now);
    var processing = processingDate(now);
    var groundDays = groundDaysForZip(opts.zip);

    return {
      now: now,
      processing: processing,
      state: stateForZip(opts.zip),
      /* A ZIP we can read but do not ship free to (AK/HI/PR/military). */
      deliverable: Boolean(groundDays),
      groundDays: groundDays,
      secondsUntilCutoff: secondsUntilCutoff(now),

      /* True only when today really is the ship day — a weekday, before the noon
         ET cutoff. Gates every "if you order today" claim on the storefront.
         Lives here so the PDP tile and the service-date modal cannot disagree. */
      shipsToday: ordinal(processing) === ordinal(now.date),

      ground: groundDays ? addBusinessDays(processing, groundDays) : null
    };
  }

  /* ======================================================================
     Live carrier rates

     GET /cart/shipping_rates.json asks Shopify, which asks UPS, what is
     actually available to this ZIP and when each service commits to arrive.
     That is the only trustworthy answer for air.

     Three things to know about the endpoint:
       - it quotes the CURRENT CART, so an empty cart returns [] and we simply
         do not know yet
       - an unroutable ZIP comes back as {"zip":[...]}, which doubles as free
         ZIP validation
       - it costs about a second, so results are cached per ZIP
     ====================================================================== */

  var rateCache = {};

  function parseRates(payload) {
    if (!payload || !Array.isArray(payload.shipping_rates)) return null;

    return payload.shipping_rates.map(function (rate) {
      var name = String(rate.name || rate.presentment_name || "");
      var range = rate.delivery_range;

      /* The late end of the commitment window. Promise the conservative date. */
      var quoted = range && range.length ? parseISO(range[range.length - 1]) : null;

      /* DO NOT TRUST CARRIER DATES BLINDLY. Measured 2026-09-02, this endpoint
         quoted 2nd Day Air to 90210 arriving 2026-09-07 — Labor Day, when UPS
         does not deliver. Roll any such date to the next real delivery day
         rather than repeating it back to a customer as a promise. */
      var arrival = quoted && !isDeliveryDay(quoted) ? nextDeliveryDay(quoted) : quoted;

      return {
        code: rate.code || name,
        name: name.replace(/\s*®/g, ""),
        price: Number(rate.price),
        arrival: arrival,
        quoted: quoted,
        adjusted: Boolean(quoted && arrival && ordinal(arrival) !== ordinal(quoted)),

        /* The baseline is whatever costs nothing, identified by price rather
           than by matching "ground" in the name. The free flat rate has already
           been renamed once mid-flight (UPS Ground -> UPS Select); a name-based
           test would have quietly started treating it as a paid upgrade
           candidate, and at $0 it would have won every comparison. */
        isBaseline: Number(rate.price) === 0
      };
    });
  }

  /* Resolves to an array of services, or null when we could not find out.
     Never rejects — a rate lookup failing must not break the page. */
  function fetchRates(zip) {
    var digits = String(zip || "").replace(/\D/g, "");
    var province = stateForZip(digits);

    if (digits.length !== 5 || !province) return Promise.resolve(null);

    /* Quotes carry absolute dates, so they are only valid for the ship day they
       were fetched against. Key the cache on that day as well as the ZIP, or a
       session that spans the noon cutoff would reuse yesterday's commitments. */
    var key = digits + "|" + toISO(processingDate(nowEastern()));

    var cached = rateCache[key];
    if (cached && Date.now() - cached.at < RATE_CACHE_MS) {
      return Promise.resolve(cached.rates);
    }

    var query =
      "shipping_address%5Bcountry%5D=United+States" +
      "&shipping_address%5Bprovince%5D=" + encodeURIComponent(province) +
      "&shipping_address%5Bzip%5D=" + encodeURIComponent(digits);

    return fetch("/cart/shipping_rates.json?" + query, { headers: { Accept: "application/json" } })
      .then(function (response) { return response.ok ? response.json() : null; })
      .then(function (payload) {
        var rates = parseRates(payload);

        /* Never cache an empty result. [] means the cart had nothing to quote,
           and that answer stops being true the moment they add to cart. */
        if (rates && rates.length) rateCache[key] = { at: Date.now(), rates: rates };

        return rates;
      })
      .catch(function () { return null; });
  }

  /* The decision. Ground comes from the table; any upgrade comes from rates.

     Pass `rates` as returned by fetchRates:
       undefined / null  we have not been able to look
       []                looked, nothing quoted (usually an empty cart)
       [...]             live quotes

     status:
       no_zip            we do not know where it is going yet
       off_network       AK / HI / PR / military / unreadable ZIP
       no_date           destination known, no service date given
       ground_ok         free ground already beats the deadline
       rush_confirmed    UPS quoted a service that lands in time, free upgrade
       rush_unconfirmed  ground misses and we have no quotes yet
       call_us           UPS quoted nothing that lands in time
  */
  function plan(options) {
    var opts = options || {};
    var base = estimate(opts);

    if (!String(opts.zip || "").replace(/\D/g, "")) {
      return { status: "no_zip", estimate: base };
    }

    if (!base.deliverable) {
      return { status: "off_network", estimate: base };
    }

    var service = parseISO(opts.serviceDate);

    if (!service) {
      return {
        status: "no_date",
        estimate: base,
        serviceLabel: GROUND_LABEL,
        arrival: base.ground
      };
    }

    /* Never promise an arrival on the day of the service itself. */
    var deadline = ordinal(addDays(service, -BUFFER_DAYS));

    /* The physical floor, checked BEFORE anything else. Nothing leaves before
       the processing date, and the fastest thing we can buy still needs a
       business day after that. If even that misses the deadline, no carrier
       quote can rescue it — so say so outright rather than falling through to
       rush_unconfirmed, which would promise an upgrade that cannot exist.

       This is what let a 90210 order placed after the cutoff, for a service the
       next day, come back as "free rush upgrade, fastest service that arrives
       in time". It ships Sep 3 at the earliest and arrives Sep 4 at the
       earliest, against a Sep 2 deadline. */
    var earliest = addBusinessDays(base.processing, 1);

    if (ordinal(earliest) > deadline) {
      return {
        status: "call_us",
        impossible: true,
        estimate: base,
        earliest: earliest,
        groundArrival: base.ground,
        serviceDate: service
      };
    }

    if (base.ground && ordinal(base.ground) <= deadline) {
      return {
        status: "ground_ok",
        estimate: base,
        serviceLabel: GROUND_LABEL,
        arrival: base.ground,
        groundArrival: base.ground,
        serviceDate: service,
        upgraded: false
      };
    }

    /* Ground misses. Only live quotes can say what actually beats the date. */
    var rates = opts.rates;

    if (!Array.isArray(rates) || !rates.length) {
      return {
        status: "rush_unconfirmed",
        unconfirmedReason: "no_quotes",
        estimate: base,
        groundArrival: base.ground,
        serviceDate: service,
        upgraded: true
      };
    }

    /* Quotes without dates are common — this endpoint has been seen returning
       "UPS Select" with no delivery_range at all. That means we do not know,
       which is NOT the same as "nothing is fast enough". Never refuse a sale
       because the carrier withheld a date. */
    var dated = rates.filter(function (rate) { return !rate.isBaseline && rate.arrival; });

    if (!dated.length) {
      return {
        status: "rush_unconfirmed",
        unconfirmedReason: "no_dates",
        estimate: base,
        groundArrival: base.ground,
        serviceDate: service,
        upgraded: true
      };
    }

    var qualifying = dated.filter(function (rate) {
      return ordinal(rate.arrival) <= deadline;
    }).sort(function (a, b) { return a.price - b.price; });

    if (!qualifying.length) {
      /* Report the best UPS could actually do, so the copy can be specific
         about why we are asking them to call. */
      var best = dated.slice().sort(function (a, b) {
        return ordinal(a.arrival) - ordinal(b.arrival);
      })[0];

      return {
        status: "call_us",
        estimate: base,
        groundArrival: base.ground,
        serviceDate: service,
        fastest: best || null,
        serviceLabel: best ? best.name : null,
        arrival: best ? best.arrival : null
      };
    }

    var chosen = qualifying[0];

    return {
      status: "rush_confirmed",
      estimate: base,
      serviceLabel: chosen.name,
      arrival: chosen.arrival,
      cost: chosen.price,
      groundArrival: base.ground,
      serviceDate: service,
      upgraded: true
    };
  }

  /* ======================================================================
     Formatting
     ====================================================================== */

  function formatDate(date, style) {
    if (!date) return "";

    var options = style === "long"
      ? { weekday: "long", month: "long", day: "numeric" }
      : { weekday: "short", month: "short", day: "numeric" };

    return new Intl.DateTimeFormat("en-US", options).format(toUTC(date));
  }

  function formatCountdown(totalSeconds) {
    var hours = Math.floor(totalSeconds / 3600);
    var minutes = Math.floor((totalSeconds % 3600) / 60);
    var seconds = totalSeconds % 60;

    return [hours, minutes, seconds].map(function (part) {
      return String(part).padStart(2, "0");
    }).join(" : ");
  }

  /* Calendar days between today (Eastern) and a service date. */
  function daysUntil(serviceDate, now) {
    var service = parseISO(serviceDate);
    if (!service) return null;

    var today = (now || nowEastern()).date;
    return Math.round((toUTC(service) - toUTC(today)) / 86400000);
  }

  /* ======================================================================
     Stored destination. The ZIP the shopper told us, remembered across
     pages so the estimate does not reset on every navigation.
     ====================================================================== */

  var ZIP_KEY = "hmm:delivery-zip";

  function readZip() {
    try {
      return window.localStorage.getItem(ZIP_KEY) || "";
    } catch (error) {
      return "";
    }
  }

  function writeZip(zip) {
    var digits = String(zip || "").replace(/\D/g, "").slice(0, 5);

    try {
      if (digits.length === 5) window.localStorage.setItem(ZIP_KEY, digits);
      else window.localStorage.removeItem(ZIP_KEY);
    } catch (error) {
      /* Private browsing. The estimate still works, it just will not persist. */
    }

    return digits;
  }

  window.HMDelivery = {
    CUTOFF_HOUR_ET: CUTOFF_HOUR_ET,
    ORIGIN_ZIP: ORIGIN_ZIP,
    BUFFER_DAYS: BUFFER_DAYS,
    GROUND_DAYS: GROUND_DAYS,
    GROUND_LABEL: GROUND_LABEL,

    nowEastern: nowEastern,
    processingDate: processingDate,
    secondsUntilCutoff: secondsUntilCutoff,
    isBusinessDay: isBusinessDay,
    addBusinessDays: addBusinessDays,
    stateForZip: stateForZip,
    groundDaysForZip: groundDaysForZip,
    estimate: estimate,
    fetchRates: fetchRates,
    plan: plan,
    daysUntil: daysUntil,

    parseISO: parseISO,
    toISO: toISO,
    addDays: addDays,
    formatDate: formatDate,
    formatCountdown: formatCountdown,

    readZip: readZip,
    writeZip: writeZip
  };
})();
